import React, { useEffect, useRef, useState } from "react";
import "./QuantumResearchShared.css";

/* Mini Dot with glow and phase coloring */
function MiniDot({ x, y, phase, coher }) {
  const hue = ((phase % (2 * Math.PI)) / (2 * Math.PI)) * 360;
  const size = 6 + coher * 8;
  const opacity = 0.35 + coher * 0.65;
  const fill = `hsla(${hue}, 90%, ${40 + coher * 30}%, ${opacity})`;
  return (
    <svg className="mini-dot" viewBox="0 0 24 24" width="32" height="32">
      <circle cx="12" cy="12" r={size} fill={fill} stroke="#111" strokeWidth="0.5" />
    </svg>
  );
}

export default function QuantumNoiseVisualizer({ rows = 8, cols = 8, cellGap = 10 }) {
  const TOTAL = rows * cols;
  const [phases, setPhases] = useState(Array(TOTAL).fill(0));
  const [coherences, setCoherences] = useState(Array(TOTAL).fill(1));
  const [autoNoise, setAutoNoise] = useState(false);
  const [coherenceHistory, setCoherenceHistory] = useState([]);
  const raf = useRef(null);

  /* Animation loop for autoNoise */
  useEffect(() => {
    let t = 0;
    function step() {
      t += 1;
      if (autoNoise) {
        setPhases((prev) =>
          prev.map((p, i) => p + (Math.sin((t + i) * 0.01) * 0.02 + (Math.random() - 0.5) * 0.01))
        );
        setCoherences((prev) =>
          prev.map((c) => Math.max(0.3, Math.min(1, c - 0.0005 + Math.random() * 0.001)))
        );
      }
      // Update average coherence history
      setCoherenceHistory((prev) => {
        const avg = coherences.reduce((a, b) => a + b, 0) / TOTAL;
        return [...prev.slice(-49), avg];
      });

      raf.current = requestAnimationFrame(step);
    }
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [autoNoise, coherences]);

  /* Add radial noise pulse */
  const addNoisePulse = (centerIdx = null, strength = 1.0, radius = Math.max(rows, cols)) => {
    const center = centerIdx == null ? { r: Math.floor(rows / 2), c: Math.floor(cols / 2) } : { r: Math.floor(centerIdx / cols), c: centerIdx % cols };
    setPhases((prev) =>
      prev.map((p, i) => {
        const r = Math.floor(i / cols), c = i % cols;
        const dist = Math.sqrt((r - center.r) ** 2 + (c - center.c) ** 2);
        const atten = Math.exp(-dist / radius);
        return p + (Math.random() - 0.5) * 1.2 * strength * atten + Math.sin(dist + Date.now() * 0.002) * 0.15 * atten;
      })
    );
    setCoherences((prev) =>
      prev.map((c, i) => {
        const r = Math.floor(i / cols), cidx = i % cols;
        const dist = Math.sqrt((r - center.r) ** 2 + (cidx - center.c) ** 2);
        const atten = Math.exp(-dist / (radius * 0.6));
        return Math.max(0.12, prev[i] - 0.3 * strength * atten);
      })
    );
  };

  const errorCorrect = (centerIdx = null) => {
    const center = centerIdx == null ? { r: Math.floor(rows / 2), c: Math.floor(cols / 2) } : { r: Math.floor(centerIdx / cols), c: centerIdx % cols };
    setPhases((prev) =>
      prev.map((p, i) => {
        const r = Math.floor(i / cols), c = i % cols;
        const dist = Math.sqrt((r - center.r) ** 2 + (c - center.c) ** 2);
        const atten = Math.exp(-dist / 3);
        return p * (1 - 0.9 * atten);
      })
    );
    setCoherences((prev) =>
      prev.map((c, i) =>
        Math.min(1, c + 0.35 * Math.exp(-((Math.floor(i / cols) - center.r) ** 2 + (i % cols - center.c) ** 2) / 6))
      )
    );
  };

  const onGridClick = (idx) => addNoisePulse(idx, 1.2, 4);

  return (
    <div className="qr-root">
      <div className="qr-bg-pulse"></div>
      <h2 className="qr-title">🔊 Quantum Noise Visualizer</h2>
      <p className="qr-subtitle">
        See how phase noise spreads across a qubit lattice. Click a cell to inject a local noise pulse.
      </p>

      {/* Controls */}
      <div className="qr-controls">
        <button className="qr-run-btn" onClick={() => addNoisePulse(null, 1.2, Math.max(rows, cols))}>Center Noise</button>
        <button className="qr-run-btn" onClick={() => addNoisePulse(null, 0.6, Math.max(rows, cols) * 1.5)}>Small Pulse</button>
        <button className="qr-run-btn" onClick={() => errorCorrect(null)}>Error Correct</button>
        <button className="qr-run-btn" onClick={() => { setPhases(Array(TOTAL).fill(0)); setCoherences(Array(TOTAL).fill(1)); setCoherenceHistory([]); }}>Reset</button>
        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input type="checkbox" checked={autoNoise} onChange={(e) => setAutoNoise(e.target.checked)} /> Auto noise
        </label>
      </div>

      {/* Side by side layout */}
      <div className="qr-panels">
        {/* Qubit grid */}
        <div className="qr-bloch" style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 40px)`, gap: cellGap }}>
          {Array.from({ length: TOTAL }).map((_, i) => (
            <div key={i} onClick={() => onGridClick(i)}>
              <MiniDot x={i % cols} y={Math.floor(i / cols)} phase={phases[i]} coher={coherences[i]} />
            </div>
          ))}
        </div>

        {/* Charts and stats panel */}
        <div className="qr-stats">
          <h4>Average Coherence</h4>
          <svg width="100%" height="100" style={{ background: "#0008", borderRadius: 6 }}>
            <polyline
              fill="none"
              stroke="#0ff"
              strokeWidth="2"
              points={coherenceHistory.map((c, i) => `${(i / 50) * 200},${100 - c * 100}`).join(" ")}
            />
          </svg>

          <table className="qr-table">
            <thead>
              <tr>
                <th>Row</th>
                <th>Avg Coherence</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: rows }).map((_, r) => {
                const rowCoher = coherences.slice(r * cols, r * cols + cols);
                const avg = (rowCoher.reduce((a, b) => a + b, 0) / cols).toFixed(2);
                return (
                  <tr key={r}>
                    <td>{r + 1}</td>
                    <td>{avg}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="qr-note">
        Click cells to inject local noise. Use Error Correct to restore coherence.
      </div>
    </div>
  );
}
